import type { PropsWithChildren, ReactNode } from 'react';
import { Suspense } from 'react';
import { matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { AppNavigation } from '@sorare/core/src/components/navigation/AppNavigation';
import { UserFlags } from '@sorare/core/src/components/user/UserFlags';
import {
  ACTIVITY,
  ANY_SPORT_ESSENCE_TYPE,
  ANY_SPORT_MARKET,
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
  ANY_SPORT_PLAYERS_SLUG_CARDS,
  FOOTBALL_CLUBS_SLUG_CARDS,
  FOOTBALL_COUNTRIES_COUNTRYCODE_CARDS,
  FOOTBALL_LEAGUES_SLUG_CARDS,
  MLB_TEAMS_SLUG_CARDS,
  NBA_TEAMS_SLUG_CARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useEnableSeriesNavigationUpdate } from '@sorare/core/src/hooks/navigation/useEnableSeriesNavigationUpdate';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { MultiSportFooter } from '@sorare/core/src/routing/MultiSportFooter';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { NewAppHeader } from 'components/navigation/NewAppHeader';

const Wrapper = styled.div`
  display: flex;
  min-height: var(--100vh);
  flex-direction: column-reverse;
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;

const PortalReceiver = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  justify-content: flex-start;
  align-items: stretch;
  position: sticky;
  bottom: 0;
  right: 0;
`;

// min-width: 0; used to override default min-width: content; Prevents content from overflowing window container (Firefox)
const MainWrapper = styled.div`
  flex: 1;
  overflow: clip;
  min-width: 0;
`;

const Content = styled.main`
  isolation: isolate;
  position: relative;
  &.displayFooter {
    min-height: calc(100vh - var(--current-stack-height));
  }
`;

const PageBackgroundPortal = styled.div`
  position: absolute;
  isolation: isolate;
  z-index: -1;
  inset: 0;
  pointer-events: none;
`;

const FOOTBALL_MARKET = generateSportPath(ANY_SPORT_MARKET, {
  sport: Sport.FOOTBALL,
});

const MOBILE_PAGES_WITHOUT_FOOTER = [ANY_SPORT_ESSENCE_TYPE];

const ANY_SPORT_PAGES_WITHOUT_FOOTER = [
  ANY_SPORT_PLAYERS_SLUG_CARDS,
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
];

const PAGES_WITHOUT_FOOTER = [
  FOOTBALL_CLUBS_SLUG_CARDS,
  FOOTBALL_LEAGUES_SLUG_CARDS,
  FOOTBALL_COUNTRIES_COUNTRYCODE_CARDS,
  NBA_TEAMS_SLUG_CARDS,
  MLB_TEAMS_SLUG_CARDS,
  ACTIVITY,
  (pathname: string) =>
    matchPath(catchAll(FOOTBALL_MARKET), pathname) &&
    !matchPath(FOOTBALL_MARKET, pathname),
];

const useDisplayFooter = () => {
  const { pathname } = useLocation();
  const { isIosApp } = useIsMobileApp();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { matchAnySportPath } = useSportContext();

  return (
    !isIosApp &&
    !ANY_SPORT_PAGES_WITHOUT_FOOTER.some(path =>
      matchAnySportPath(path, pathname)
    ) &&
    !PAGES_WITHOUT_FOOTER.some(path =>
      typeof path === 'string' ? matchPath(path, pathname) : path(pathname)
    ) &&
    (isTabletAndAbove ||
      MOBILE_PAGES_WITHOUT_FOOTER.every(
        path => !matchAnySportPath(path, pathname)
      ))
  );
};

export const AppLayout = ({
  children,
  extraHeaderButton,
}: PropsWithChildren<{ extraHeaderButton?: ReactNode }>) => {
  const displayFooter = useDisplayFooter();
  const isLaptopAndAbove = useIsLaptop();
  const enableSeriesNavigationUpdate = useEnableSeriesNavigationUpdate();

  return (
    <Wrapper>
      <AppNavigation />
      <MainWrapper>
        <ContentWithStickyHeader
          header={<NewAppHeader extraHeaderButton={extraHeaderButton} />}
          transparentHeader={
            !!enableSeriesNavigationUpdate && !isLaptopAndAbove
          }
        >
          {!isLaptopAndAbove && <PageBackgroundPortal id="page-background" />}
          <Content>
            <UserFlags />
            {!!isLaptopAndAbove && (
              <PageBackgroundPortal id="page-background" />
            )}
            <Suspense fallback={<LoadingIndicator fullHeight />}>
              {children}
            </Suspense>
          </Content>
          {!!displayFooter && <MultiSportFooter />}
        </ContentWithStickyHeader>
        {!!isLaptopAndAbove && <PortalReceiver id="above-bottom-bar-portal" />}
      </MainWrapper>
    </Wrapper>
  );
};
